import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { SMRTabEnum } from "../../../constants/enums/smr-tab-enum";
import { useStore } from "../../../contexts/store-provider";
import { getHeadquarterIdFromLocalStorage } from "../../../helpers/localstorage-helper";
import { IMedicalRecordImagesList } from "../../../models/response/ISMRResponse";
import BeforeAfter from "./Image/image-container";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import { formatMessage } from "../../../translations/format-message";
import toast from "react-hot-toast";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import ReservationMemoCard from "./reservation-memo-card";
import CounselingNoteCard from "./counseling-note-card";
import { DATE_FORMAT } from "../../../constants/constants";
import moment from "moment";
import TreatmentMenuCard from "./treatment-menu-card";
import Nodata from "../../../shared-components/no-data-svg/no-data-svg";
import { FormattedMessage } from "react-intl";
interface IProps {
  customerId: number;
  storeId: number;
  cardRef: any;
  viewOnly: boolean;
}

const MedicalRecord = (props: IProps) => {
  const { customerId, storeId, viewOnly, cardRef } = props;
  const { smrStore, masterManagementStore, headquarterStore } = useStore();
  const {
    GetBookingListByCustomerIdService,
    allBookingList,
    GetMedicalRecordListService,
    medicalRecordListState,
    allMedicalRecord,
    activeTab,
    medicalRecordImageState,
    addMedicalRecordImageState,
    resetAddMedicalRecordImageState,
    updateMedicalRecordImageState,
    resetUpdateMedicalRecordImageState,
    updateMedicalRecordState,
    resetUpdateMedicalRecordState,
    updateMedicalRecordOrderIdState,
    resetUpdateMedicalRecordOrderIdState,
    deleteMedicalRecordImageState,
    resetDeleteMedicalRecordImageState,
    UpdateMedicalRecordService,
    setSelectedBookingId,
    getInitailAddSaleRecordDetails,
    setMedicalRecordIsScrollUp,
    setMedicalRecordOffsetId,
    medicalRecordOffsetId,
    resetMedicalRecordListState,
    toMedicalRecordFromBookingCard,
    setToMedicalRecordFromBookingCard,
    selectedBookingId
  } = smrStore;
  const { GetAllStaffOptionServiceByStoreId, GetConsumptionTaxByHeadquarterIdService } =
    masterManagementStore;
  const { GetHeadquarterTemplatesByIdService, resetHeadquarterTemplatesState, headquarterTemplatesState} =
    headquarterStore;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const isManualScrolling = useRef(false);


  useEffect(() => {
    if (activeTab === SMRTabEnum.Medical && allBookingList.length > 0) {
      setMedicalRecordOffsetId(allBookingList[0].Id + 1);
      setMedicalRecordIsScrollUp(false);
      GetMedicalRecordListService(
        customerId,
        storeId,
        allBookingList[0].Id + 1,
        false
      );
    }
  }, [activeTab]);

  useEffect(() => {
    return () => {
      resetMedicalRecordListState();
      resetHeadquarterTemplatesState();
    };
  }, []);

  const headquarterIdFromLocalStorage = getHeadquarterIdFromLocalStorage();

  useEffect(() => {
    if (activeTab === SMRTabEnum.Medical) {
      GetAllStaffOptionServiceByStoreId(props.storeId);
      GetBookingListByCustomerIdService(customerId, storeId);
      if (headquarterIdFromLocalStorage > 0){
        GetConsumptionTaxByHeadquarterIdService(headquarterIdFromLocalStorage);
        // GetHeadquarterTemplatesByIdService(headquarterIdFromLocalStorage);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      activeTab === SMRTabEnum.Medical &&
      addMedicalRecordImageState.success
    ) {
      resetAddMedicalRecordImageState();
      toast.success(formatMessage("images_uploaded_success"));
      GetMedicalRecordListService(
        customerId,
        storeId,
        medicalRecordOffsetId,
        false,
        true
      );
    }
  }, [activeTab, addMedicalRecordImageState.success]);

  useEffect(() => {
    if (
      activeTab === SMRTabEnum.Medical &&
      updateMedicalRecordImageState.success
    ) {
      toast.success(formatMessage("image_updated_success"));
      GetMedicalRecordListService(
        customerId,
        storeId,
        medicalRecordOffsetId,
        false,
        true
      );
      resetUpdateMedicalRecordImageState();
    }
  }, [activeTab, updateMedicalRecordImageState.success]);

  useEffect(() => {
    if (activeTab === SMRTabEnum.Medical && updateMedicalRecordState.success) {
      toast.success(formatMessage("record_updated_success"));
      GetMedicalRecordListService(
        customerId,
        storeId,
        medicalRecordOffsetId,
        false,
        true
      );
      resetUpdateMedicalRecordState();
    }
  }, [activeTab, updateMedicalRecordState.success]);

  useEffect(() => {
    if (
      activeTab === SMRTabEnum.Medical &&
      updateMedicalRecordOrderIdState.success
    ) {
      // toast.success(formatMessage("image_deleted_success"));
      GetMedicalRecordListService(
        customerId,
        storeId,
        medicalRecordOffsetId,
        false,
        true
      );
      resetUpdateMedicalRecordOrderIdState();
    }
  }, [activeTab, updateMedicalRecordOrderIdState.success]);

  useEffect(() => {
    if (
      activeTab === SMRTabEnum.Medical &&
      deleteMedicalRecordImageState.success
    ) {
      toast.success(formatMessage("image_deleted_success"));
      GetMedicalRecordListService(
        customerId,
        storeId,
        medicalRecordOffsetId,
        false,
        true
      );
      resetDeleteMedicalRecordImageState();
    }
  }, [activeTab, deleteMedicalRecordImageState.success]);

  useEffect(() => {
    if(medicalRecordListState.inProgress) return;
    if (activeTab === SMRTabEnum.Medical) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (isManualScrolling.current) return; // ignore while auto-scrolling

          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const smrId = Number(entry.target.getAttribute("data-smr-id"));
              const record = allMedicalRecord.find((r: IMedicalRecordImagesList) => r.SMRId === smrId);
              if (record?.SMRId) {
                setSelectedBookingId(record.SMRId);
              }
            }
          });
        },
        { threshold: 0.6  }
      );

      // Observe each record card
      Object.values(cardRef.current).forEach((el: any) => {
        if (el) observer.observe(el);
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [activeTab, allMedicalRecord, medicalRecordListState.inProgress]);

  const scrollToRecord = (id: number) => {
    const el = cardRef.current[id];
    if (el) {
      isManualScrolling.current = true; // lock observer
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // release lock after a short delay
      setTimeout(() => {
        isManualScrolling.current = false;
      }, 2000); // match smooth scroll duration
    }
  };

  
  useEffect(() => {
    if (activeTab === SMRTabEnum.Medical && selectedBookingId > 0) {
      // Delay a bit to ensure DOM nodes are rendered
      setTimeout(() => {
        scrollToRecord(selectedBookingId);
      }, 300);
    }
  }, [activeTab, selectedBookingId, allMedicalRecord]);


  return (
    <>
      {/* Data from this row will be used for medical records loop */}
      <div className="px-3 py-2" ref={containerRef}>
        {(medicalRecordListState.inProgress ||
          addMedicalRecordImageState.inProgress ||
          updateMedicalRecordImageState.inProgress ||
          medicalRecordImageState.inProgress ||
          updateMedicalRecordState.inProgress ||
          updateMedicalRecordOrderIdState.inProgress ||
          headquarterTemplatesState.inProgress ||
          deleteMedicalRecordImageState.inProgress) && (
          <FormLoader
            loading={
              medicalRecordListState.inProgress ||
              addMedicalRecordImageState.inProgress ||
              updateMedicalRecordImageState.inProgress ||
              medicalRecordImageState.inProgress ||
              updateMedicalRecordState.inProgress ||
              updateMedicalRecordOrderIdState.inProgress ||
              headquarterTemplatesState.inProgress ||
              deleteMedicalRecordImageState.inProgress
            }
          />
        )}
        {allMedicalRecord?.length > 0 && allBookingList.length > 0 ? (
          allMedicalRecord.map(
            (record: IMedicalRecordImagesList, index: number) => {
              const booking = allBookingList[index];
              const hasBooking = Boolean(booking);

              const saleRecordInfo =
                hasBooking && booking?.SaleRecord?.length > 0
                  ? booking.SaleRecord[0]
                  : getInitailAddSaleRecordDetails;

              const saleItemTreatments = saleRecordInfo?.SaleItem?.length
                ? [
                    ...saleRecordInfo?.SaleItem?.filter(
                      (item) => (item?.ItemType === 0)
                    ),
                  ]
                : [];
              const saleItemProducts = saleRecordInfo?.SaleItem?.length
                ? [
                    ...saleRecordInfo?.SaleItem?.filter(
                      (item) => (item?.ItemType === 1)
                    ),
                  ]
                : [];
              const saleItemOptions = saleRecordInfo?.SaleItem?.length
                ? [
                    ...saleRecordInfo?.SaleItem?.filter(
                      (item) => (item?.ItemType === 2)
                    ),
                  ]
                : [];

              return (
                <div
                  className="row"
                  key={record.SMRId}
                  data-smr-id={record.SMRId}
                  ref={(el) => {
                    cardRef.current[record.SMRId] = el;
                  }}
                >
                  <div className="col-md-8 col-lg-9 col-12">
                    {/* Date */}
                    {/* <h3 className="mb-2">
                      {booking.Id}
                    </h3> */}
                    <h3 className="mb-2">
                      {moment(booking?.Date).format(DATE_FORMAT)}
                    </h3>
                    {/* Menu Section */}
                    <TreatmentMenuCard
                      saleItems={saleRecordInfo?.SaleItem || []}
                      treatments={saleItemTreatments}
                      products={saleItemProducts}
                      options={saleItemOptions}
                      saleRecordInfo={saleRecordInfo}
                    />
                    {/* Karute Section */}
                    <div className="border p-3 mb-4">
                      <DndProvider backend={HTML5Backend}>
                        <h3 className="mb-3">
                          {moment(booking?.Date).format(DATE_FORMAT)}
                        </h3>
                        <div className="position-relative">
                          <div className="beforeAfterWrapper">
                            <BeforeAfter
                              type="Before"
                              viewOnly={viewOnly}
                              smrId={record.SMRId}
                              medicalRecordId={record.MedicalRecordId}
                              medicalRecordImages={record.ImageBefore}
                              medicalRecordTemplate={record.TemplateBefore}
                            />
                            <BeforeAfter
                              type="After"
                              viewOnly={viewOnly}
                              smrId={record.SMRId}
                              medicalRecordId={record.MedicalRecordId}
                              medicalRecordImages={record.ImageAfter}
                              medicalRecordTemplate={record.TemplateAfter}
                            />
                          </div>
                        </div>
                      </DndProvider>
                    </div>
                  </div>
                  <div className="col-md-4 col-lg-3 col-12">
                    <CounselingNoteCard
                      initialValue={record.CounselingNote}
                      rows={8}
                      disabled={viewOnly}
                      submitHandler={(value) => {
                        UpdateMedicalRecordService({
                          SMRId: record.SMRId,
                          ReservationMemo: record.ReservationMemo,
                          CounselingNote: value,
                        });
                      }}
                    />
                    <ReservationMemoCard
                      initialValue={record.ReservationMemo}
                      rows={3}
                      disabled={viewOnly}
                      submitHandler={(value) => {
                        UpdateMedicalRecordService({
                          SMRId: record.SMRId,
                          ReservationMemo: value,
                          CounselingNote: record.CounselingNote,
                        });
                      }}
                    />
                  </div>
                </div>
              );
            }
          )
        ) : (
          <div className="text-danger text-center no-data">
            <Nodata />
            <p className="text-danger">
              <strong>
                <FormattedMessage id="no_data_available" />
              </strong>
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default observer(MedicalRecord);
